import React, { useState } from "react";
import {
  Box,
  Divider,
  Drawer,
  Fab,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

// Mobile Actions FAB + bottom-sheet ("Actions" sheet) shared by the three
// service profiles (Interpretation / Translation / Other Service).
//
// PLACEMENT RULE (single source of truth — never override per profile):
// viewport-fixed, bottom-right, with ONE constant 24px visual gap above
// whatever bottom chrome the screen shows:
//   • xs (<600px) — no app bottom bar → 24px from the screen edge;
//   • sm (600–899px) — the app's 20px bottom bar is visible (Layout.jsx
//     renders it from sm up) → 44px (bar + the same 24px gap);
//   • plus safe-area insets for iOS home-indicator/notch devices (0 today
//     — index.html has no `viewport-fit=cover`; future-proof).
// All three profiles mount this component inside their identical mobile
// shell (top Tabs + scroll container), so the FAB lands at the exact same
// spot on every profile. Desktop never renders it (profile mobile branch
// only). The sheet chrome mirrors the card-mode bottom sheets in
// `components/DataGridResponsive.jsx` (same 12px top radius, 70vh cap,
// header row with close IconButton) so every mobile sheet in the portal
// looks the same. Children = the screen's action content (unchanged).
const SAFE_BOTTOM = "env(safe-area-inset-bottom, 0px)";
const SAFE_RIGHT = "env(safe-area-inset-right, 0px)";

// Exported for the profiles' mobile scroll containers (the mobileScrollRef
// Box): extra bottom padding so the last content row (History) always
// scrolls fully clear of the FAB — 32px of daylight at max scroll on BOTH
// bands (the FAB occupies 72px above the viewport bottom on xs, 92px on sm;
// the scroll box bottom sits 8px above the profile edge — 28px on sm once
// the bar is accounted for — so 96px of padding clears both by 32px).
export const FAB_SCROLL_CLEARANCE_SX = {
  pb: `calc(96px + ${SAFE_BOTTOM})`,
};

const SHEET_MAX_HEIGHT = "70vh";
const SHEET_PAPER_SX = {
  maxHeight: SHEET_MAX_HEIGHT,
  borderTopLeftRadius: "12px",
  borderTopRightRadius: "12px",
  borderTop: 1,
  borderRight: "none",
  borderBottom: "none",
  borderLeft: "none",
  borderColor: "divider",
};

const ServiceProfileActionsFab = ({ children }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Fab
        color="primary"
        aria-label="Actions"
        onClick={handleOpen}
        sx={{
          position: "fixed",
          // Same 24px visual gap above the bottom chrome on every screen:
          // the screen edge on xs, or above the 20px app bottom bar on sm
          // (Layout renders it from sm up) + safe-area insets.
          bottom: {
            xs: `calc(24px + ${SAFE_BOTTOM})`,
            sm: `calc(44px + ${SAFE_BOTTOM})`,
          },
          right: `calc(24px + ${SAFE_RIGHT})`,
          zIndex: (theme) => theme.zIndex.fab,
          borderRadius: "12px",
          width: 48,
          height: 48,
        }}
      >
        <MoreVertRoundedIcon
          sx={{ color: theme.palette.background.default, fontSize: 24 }}
        />
      </Fab>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: SHEET_PAPER_SX } }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            maxHeight: SHEET_MAX_HEIGHT,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
              Actions
            </Typography>
            <IconButton size="small" aria-label="Close" onClick={handleClose}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
            {children}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default ServiceProfileActionsFab;
